// The share modal's one recipients field (handoff 2b): chips for typed/pasted emails or phone numbers, and —
// for admin / internal_staff only — coach search (/admin/users?role=coach) that adds a coach chip.
// Chip: { kind: 'coach', id, label, email, phone } | { kind: 'email'|'phone', value, invalid }.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

// Split pasted/typed text into entries on commas, semicolons and newlines; a piece with spaces is split on
// them too unless it is one phone number written with spaces, e.g. "(312) 555-1212".
export const splitEntries = (text) => String(text || '').split(/[,;\n\r]+/)
  .flatMap((piece) => (isPhone(piece) ? [piece] : piece.split(/\s+/)))
  .map((s) => s.trim()).filter(Boolean);
export const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
export const isPhone = (s) => {
  const digits = String(s || '').replace(/[+()\-.\s]/g, '');
  return /^\d+$/.test(digits) && digits.length >= 10;
};

// Why a chip cannot be sent on this channel, or null when it can.
export function chipError(chip, channel) {
  if (chip.kind === 'coach') {
    return channel === 'text' && !chip.phone ? `No phone number on file for ${chip.label}.` : null;
  }
  if (channel === 'text') return isPhone(chip.value) ? null : "That doesn't look like a phone number.";
  return isEmail(chip.value) ? null : "That doesn't look like an email address.";
}

export const plainChip = (value, channel) => {
  const kind = isEmail(value) ? 'email' : isPhone(value) ? 'phone' : (channel === 'text' ? 'phone' : 'email');
  const chip = { kind, value };
  return { ...chip, invalid: chipError(chip, channel) !== null };
};

const coachName = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
const sameChip = (a, b) => (a.kind === 'coach' || b.kind === 'coach'
  ? a.kind === b.kind && a.id === b.id
  : a.value.toLowerCase() === b.value.toLowerCase());

export default function RecipientField({ channel, chips, onChange, canSearchCoaches, autoFocus }) {
  const { API_URL } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (autoFocus && inputRef.current) inputRef.current.focus(); }, [autoFocus]);

  useEffect(() => {
    const q = query.trim();
    if (!canSearchCoaches || q.length < 2) { setResults([]); setSearching(false); return undefined; }
    let live = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/users?search=${encodeURIComponent(q)}&role=coach&limit=8`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        const items = Array.isArray(res.data) ? res.data : (res.data.items || []);
        if (live) setResults(items);
      } catch (e) {
        if (live) setResults([]);
      }
      if (live) setSearching(false);
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [query, canSearchCoaches, API_URL]);

  const add = (newChips) => {
    const next = [...chips];
    newChips.forEach((c) => { if (!next.some((x) => sameChip(x, c))) next.push(c); });
    onChange(next);
  };
  const commit = (text) => {
    const entries = splitEntries(text);
    if (entries.length) add(entries.map((v) => plainChip(v, channel)));
    setQuery('');
  };
  const pickCoach = (u) => {
    add([{ kind: 'coach', id: u.id, label: coachName(u), email: u.email || null, phone: u.phone || null }]);
    setQuery('');
    if (inputRef.current) inputRef.current.focus();
  };
  const removeAt = (i) => onChange(chips.filter((_, idx) => idx !== i));

  const onInput = (e) => {
    const v = e.target.value;
    if (/[,;\n]/.test(v)) commit(v); else setQuery(v);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (results.length && !isEmail(query) && !isPhone(query)) pickCoach(results[0]); else commit(query);
    } else if (e.key === 'Backspace' && query === '' && chips.length) {
      removeAt(chips.length - 1);
    }
  };
  const onPaste = (e) => {
    const text = e.clipboardData ? e.clipboardData.getData('text') : '';
    if (!text) return;
    e.preventDefault();
    commit(`${query} ${text}`);
  };
  // Leaving the field commits what was typed, unless it is a coach search still showing results
  // (clicking a result does not blur: its mousedown is cancelled).
  const onBlur = () => { if (!results.length || isEmail(query) || isPhone(query)) commit(query); };

  const placeholder = canSearchCoaches
    ? (channel === 'text' ? 'Add a number, or search coaches on ProZpects…' : 'Add another, or search coaches on ProZpects…')
    : (channel === 'text' ? 'Add a phone number' : 'Add an email address');
  const errors = [...new Set(chips.map((c) => chipError(c, channel)).filter(Boolean))];
  const showMenu = canSearchCoaches && query.trim().length >= 2 && (searching || results.length > 0);

  return (
    <div className="ls-share__block">
      <label className="ls-share__label" htmlFor="share_recipients_input">
        {channel === 'text' ? 'Phone numbers' : 'Email addresses'}
      </label>
      <div className="ls-share__field-wrap">
        <div className="ls-share__field" data-testid="share_recipients_field">
          {chips.map((c, i) => (
            <span key={c.kind === 'coach' ? `coach-${c.id}` : `v-${c.value}`} data-testid={`share_chip_${i}`}
              className={chipError(c, channel) ? 'ls-share__chip ls-share__chip--invalid' : 'ls-share__chip'}>
              {c.kind === 'coach' ? c.label : c.value}
              <button type="button" className="ls-share__chip-x" aria-label={`Remove ${c.kind === 'coach' ? c.label : c.value}`}
                data-testid={`share_chip_remove_${i}`} onClick={() => removeAt(i)}>×</button>
            </span>
          ))}
          <input ref={inputRef} id="share_recipients_input" className="ls-share__input" data-testid="share_recipients_input"
            value={query} placeholder={placeholder} autoComplete="off"
            onChange={onInput} onKeyDown={onKeyDown} onPaste={onPaste} onBlur={onBlur} />
        </div>
        {showMenu && (
          <div className="ls-share__menu" role="listbox" data-testid="share_coach_menu">
            {searching && !results.length && <div className="ls-share__menu-head">Searching…</div>}
            {results.map((u) => (
              <button key={u.id} type="button" role="option" aria-selected="false" className="ls-share__menu-item"
                data-testid={`share_coach_${u.id}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pickCoach(u)}>
                <span className="ls-share__menu-name">{coachName(u)}</span>
                <span className="ls-share__menu-sub">{channel === 'text' ? (u.phone || 'No phone on file') : u.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {errors.length > 0 && (
        <div className="ls-share__error" data-testid="share_recipient_error">
          {errors.map((m) => <div key={m}>{m}</div>)}
        </div>
      )}
    </div>
  );
}
